import { MutableRefObject, useCallback, useEffect } from 'react'

import { Draft } from '@lyrahealth-inc/shared-app-logic/src/models/draft/Draft'

import { useAppDispatch } from '../../../../data/store'
import { useCreateDraftMutation, useUpdateDraftMutation } from '../messagesApi'
import { toggleLiveMsgSession } from '../messagesSlice'
import { MessengerConversation } from '../types'

interface UseSaveMessageDraftParams {
  conversationDraftRef: MutableRefObject<Draft | undefined>
  dblAttachmentRef: MutableRefObject<boolean>
  essentialsLinkAttachmentRef: MutableRefObject<boolean>
  essentialsLinkUrlRef: MutableRefObject<string | null>
  inLiveMsgSession: boolean
  inputRef: MutableRefObject<string>
  providerId: string | undefined
  selectedClientId: string | null
  selectedConversation: MessengerConversation | null
  setInitialValue: (value: string) => void
  shouldShowMessengerDraftsUtility: boolean
  disableDrafts?: boolean
  // Set while the composer is moving from no conversation to one its send just created.
  createdConversationIdRef?: MutableRefObject<string | null>
}

/**
 * Custom hook that handles draft cleanup when the component unmounts.
 * This includes saving or updating drafts based on the current input state,
 * clearing the input reference, and handling live messaging session cleanup.
 */
export const useSaveMessageDraft = ({
  conversationDraftRef,
  dblAttachmentRef,
  essentialsLinkAttachmentRef,
  essentialsLinkUrlRef,
  inLiveMsgSession,
  inputRef,
  providerId,
  selectedClientId,
  selectedConversation,
  setInitialValue,
  shouldShowMessengerDraftsUtility,
  disableDrafts = false,
  createdConversationIdRef,
}: UseSaveMessageDraftParams) => {
  const appDispatch = useAppDispatch()
  const [updateDraft] = useUpdateDraftMutation()
  const [createDraft] = useCreateDraftMutation()

  const handleDraftSaving = useCallback(() => {
    const hasInput = Boolean(inputRef.current)
    const hasExistingDraft = Boolean(conversationDraftRef.current)
    const hasContentChanged = inputRef.current !== conversationDraftRef.current?.body
    const hasDblAttachmentStatusChanged =
      dblAttachmentRef.current !== conversationDraftRef.current?.meta_data?.dbl_attached
    const hasEssentialsLinkAttachmentStatusChanged =
      essentialsLinkAttachmentRef.current !== conversationDraftRef.current?.meta_data?.essentials_link_attached
    const hasEssentialsLinkUrlChanged =
      essentialsLinkUrlRef.current !== conversationDraftRef.current?.meta_data?.essentials_link_url
    const hasAttachmentStatusChanged =
      hasDblAttachmentStatusChanged || hasEssentialsLinkAttachmentStatusChanged || hasEssentialsLinkUrlChanged

    if (!selectedConversation?.conversation_id || !selectedClientId) return

    const hasConversationIdDiscrepancy =
      hasExistingDraft && conversationDraftRef.current?.conversation_id !== selectedConversation?.conversation_id
    const hasPatientIdDiscrepancy =
      hasExistingDraft && conversationDraftRef.current?.meta_data?.patient_lyra_id !== selectedClientId

    if (hasConversationIdDiscrepancy || hasPatientIdDiscrepancy) {
      return
    }

    if (hasExistingDraft && hasInput && (hasContentChanged || hasAttachmentStatusChanged)) {
      updateDraft({
        conversationId: conversationDraftRef.current!.conversation_id,
        draftId: conversationDraftRef.current!.id,
        data: {
          body: inputRef.current,
          meta_data: {
            patient_lyra_id: conversationDraftRef.current!.meta_data.patient_lyra_id,
            dbl_attached: dblAttachmentRef.current,
            essentials_link_attached: essentialsLinkAttachmentRef.current,
            essentials_link_url: essentialsLinkUrlRef.current,
          },
        },
      })
      return
    }

    if (!hasExistingDraft && hasInput && providerId && selectedConversation?.patient_lyra_id) {
      createDraft({
        conversationId: selectedConversation.conversation_id,
        data: {
          body: inputRef.current,
          meta_data: {
            patient_lyra_id: selectedConversation.patient_lyra_id,
            dbl_attached: dblAttachmentRef.current,
            essentials_link_attached: essentialsLinkAttachmentRef.current,
            essentials_link_url: essentialsLinkUrlRef.current,
          },
          provider_id: providerId,
        },
      })
    }
  }, [
    conversationDraftRef,
    createDraft,
    dblAttachmentRef,
    essentialsLinkAttachmentRef,
    essentialsLinkUrlRef,
    inputRef,
    providerId,
    selectedClientId,
    selectedConversation?.conversation_id,
    selectedConversation?.patient_lyra_id,
    updateDraft,
  ])

  const handleLiveSessionCleanup = useCallback(() => {
    if (inLiveMsgSession) {
      appDispatch(toggleLiveMsgSession(false))
    }
  }, [appDispatch, inLiveMsgSession])

  useEffect(
    () => () => {
      if (!shouldShowMessengerDraftsUtility) {
        return
      }
      if (!disableDrafts) {
        handleDraftSaving()
      }
      handleLiveSessionCleanup()
      // Reaching the conversation this composer just created is not a switch; keep what was typed.
      if (selectedConversation?.conversation_id || !createdConversationIdRef?.current) {
        inputRef.current = ''
        setInitialValue('')
      }
    },
    [
      appDispatch,
      createdConversationIdRef,
      disableDrafts,
      handleDraftSaving,
      handleLiveSessionCleanup,
      inputRef,
      inLiveMsgSession,
      shouldShowMessengerDraftsUtility,
      selectedClientId,
      selectedConversation?.conversation_id,
      setInitialValue,
    ],
  )
}
